import { useState } from "react";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Users,
  Home,
  Pencil,
  Trash2,
  UserPlus,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";

const ROLES = [
  { value: "COMMUNITY_ADMIN", label: "Administrador del barrio" },
  { value: "RESIDENT", label: "Residente" },
  { value: "SECURITY", label: "Seguridad" },
  { value: "MAINTENANCE", label: "Mantenimiento" },
  { value: "AMENITY_STAFF", label: "Personal de amenities" },
] as const;

const ASSIGNMENT_TYPES = [
  { value: "OWNER", label: "Propietario" },
  { value: "LONG_TERM_TENANT", label: "Inquilino permanente" },
  { value: "SEASONAL_TENANT", label: "Inquilino temporal" },
  { value: "FAMILY_MEMBER", label: "Familiar" },
] as const;

type Role = (typeof ROLES)[number]["value"];
type AssignmentType = (typeof ASSIGNMENT_TYPES)[number]["value"];

export function AdminCommunitiesPage() {
  const utils = trpc.useUtils();
  const { data: communities, isLoading } =
    trpc.admin.getAllCommunities.useQuery();
  const { data: allUsers } = trpc.admin.getAllUsers.useQuery();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingCommunity, setEditingCommunity] = useState<{
    id: string;
    name: string;
    timezone: string;
  } | null>(null);
  const [managingMembersCommunityId, setManagingMembersCommunityId] = useState<
    string | null
  >(null);
  const [newMember, setNewMember] = useState<{
    userId: string;
    role: Role;
    unitId: string;
    newUnit: { unitNumber: string; blockSection: string } | null;
    assignmentType: AssignmentType;
  }>({
    userId: "",
    role: "RESIDENT",
    unitId: "",
    newUnit: null,
    assignmentType: "OWNER",
  });

  const [newCommunity, setNewCommunity] = useState({
    name: "",
    timezone: "America/Argentina/Buenos_Aires",
  });

  const createCommunity = trpc.admin.createCommunity.useMutation();
  const updateCommunity = trpc.admin.updateCommunity.useMutation();
  const deleteCommunity = trpc.admin.deleteCommunity.useMutation();
  const addMember = trpc.admin.addCommunityMember.useMutation();
  const removeMember = trpc.admin.removeCommunityMember.useMutation();
  const assignUnit = trpc.admin.assignUnitToMember.useMutation();
  const removeAssignment = trpc.admin.removeUnitAssignment.useMutation();

  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [memberUnitForm, setMemberUnitForm] = useState<{
    unitId: string;
    newUnit: { unitNumber: string; blockSection: string } | null;
    assignmentType: AssignmentType;
  }>({ unitId: "", newUnit: null, assignmentType: "OWNER" });

  const { data: communityDetails, isLoading: detailsLoading } =
    trpc.admin.getCommunity.useQuery(
      { id: managingMembersCommunityId || "" },
      { enabled: !!managingMembersCommunityId },
    );

  const existingMemberUserIds = new Set(
    communityDetails?.members.map((m) => m.user.id) || [],
  );
  const availableUsers = allUsers?.filter(
    (u) => !existingMemberUserIds.has(u.id),
  );

  const handleCreate = async () => {
    if (!newCommunity.name.trim() || createCommunity.isPending) return;
    try {
      await createCommunity.mutateAsync(newCommunity);
      utils.admin.getAllCommunities.invalidate();
      setIsCreateOpen(false);
      setNewCommunity({ name: "", timezone: "America/Argentina/Buenos_Aires" });
      toast.success("Barrio creado");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo crear el barrio",
      );
    }
  };

  const handleUpdate = async () => {
    if (
      !editingCommunity ||
      !editingCommunity.name.trim() ||
      updateCommunity.isPending
    )
      return;
    try {
      await updateCommunity.mutateAsync({
        id: editingCommunity.id,
        name: editingCommunity.name,
        timezone: editingCommunity.timezone,
      });
      utils.admin.getAllCommunities.invalidate();
      setEditingCommunity(null);
      toast.success("Barrio actualizado");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo actualizar el barrio",
      );
    }
  };

  const handleDelete = async (communityId: string) => {
    try {
      await deleteCommunity.mutateAsync({ id: communityId });
      utils.admin.getAllCommunities.invalidate();
      toast.success("Barrio eliminado");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo eliminar el barrio",
      );
    }
  };

  const handleAddMember = async () => {
    if (!managingMembersCommunityId || !newMember.userId || addMember.isPending)
      return;
    const hasUnit = newMember.unitId || newMember.newUnit?.unitNumber;
    try {
      await addMember.mutateAsync({
        communityId: managingMembersCommunityId,
        userId: newMember.userId,
        role: newMember.role,
        unitId: newMember.unitId || undefined,
        newUnit: newMember.newUnit?.unitNumber
          ? {
              unitNumber: newMember.newUnit.unitNumber,
              blockSection: newMember.newUnit.blockSection || undefined,
            }
          : undefined,
        assignmentType: hasUnit ? newMember.assignmentType : undefined,
      });
      utils.admin.getCommunity.invalidate({ id: managingMembersCommunityId });
      utils.admin.getAllCommunities.invalidate();
      setNewMember({
        userId: "",
        role: "RESIDENT",
        unitId: "",
        newUnit: null,
        assignmentType: "OWNER",
      });
      toast.success("Miembro agregado");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo agregar el miembro",
      );
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      await removeMember.mutateAsync({ memberId });
      if (managingMembersCommunityId) {
        utils.admin.getCommunity.invalidate({ id: managingMembersCommunityId });
      }
      utils.admin.getAllCommunities.invalidate();
      toast.success("Miembro eliminado");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo eliminar el miembro",
      );
    }
  };

  const handleAssignUnit = async (memberId: string) => {
    if (!managingMembersCommunityId) return;
    const hasUnit = memberUnitForm.unitId || memberUnitForm.newUnit?.unitNumber;
    if (!hasUnit) return;

    try {
      await assignUnit.mutateAsync({
        memberId,
        unitId: memberUnitForm.unitId || undefined,
        newUnit: memberUnitForm.newUnit?.unitNumber
          ? {
              communityId: managingMembersCommunityId,
              unitNumber: memberUnitForm.newUnit.unitNumber,
              blockSection: memberUnitForm.newUnit.blockSection || undefined,
            }
          : undefined,
        assignmentType: memberUnitForm.assignmentType,
      });
      utils.admin.getCommunity.invalidate({ id: managingMembersCommunityId });
      utils.admin.getAllCommunities.invalidate();
      setMemberUnitForm({ unitId: "", newUnit: null, assignmentType: "OWNER" });
      toast.success("Unidad asignada");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo asignar la unidad",
      );
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    try {
      await removeAssignment.mutateAsync({ assignmentId });
      if (managingMembersCommunityId) {
        utils.admin.getCommunity.invalidate({ id: managingMembersCommunityId });
      }
      utils.admin.getAllCommunities.invalidate();
      toast.success("Unidad desasignada");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo desasignar la unidad",
      );
    }
  };

  const formatDate = (date: string | Date) => {
    return new Intl.DateTimeFormat("es-AR", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(date));
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8">
        <h1 className="text-2xl font-bold md:text-3xl">Panel de Admin</h1>
        <p className="text-muted-foreground">Gestioná usuarios y barrios.</p>
      </div>

      <div className="mb-6 flex gap-2">
        <Link
          to="/admin/users"
          className="rounded-xl px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Usuarios
        </Link>
        <Link
          to="/admin/communities"
          className="rounded-xl px-4 py-2 text-sm font-medium bg-primary text-primary-foreground"
        >
          Barrios
        </Link>
        <Link
          to="/admin/communications"
          className="rounded-xl px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Comunicados
        </Link>
        <Link
          to="/admin/my-access"
          className="rounded-xl px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Mi acceso
        </Link>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Gestión de barrios</CardTitle>
            <CardDescription>Creá y gestioná barrios privados.</CardDescription>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Nuevo barrio
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear barrio</DialogTitle>
                <DialogDescription>
                  Agregá un nuevo barrio a la plataforma.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre</Label>
                  <Input
                    id="name"
                    placeholder="Barrio Los Robles"
                    value={newCommunity.name}
                    onChange={(e) =>
                      setNewCommunity({ ...newCommunity, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Zona horaria</Label>
                  <Input
                    id="timezone"
                    placeholder="America/Argentina/Buenos_Aires"
                    value={newCommunity.timezone}
                    onChange={(e) =>
                      setNewCommunity({
                        ...newCommunity,
                        timezone: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleCreate}
                  disabled={
                    createCommunity.isPending || !newCommunity.name.trim()
                  }
                >
                  {createCommunity.isPending ? "Creando..." : "Crear"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {communities?.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Todavía no hay barrios. Creá uno para empezar.
            </p>
          ) : (
            <div className="space-y-4">
              {communities?.map((community) => (
                <div
                  key={community.id}
                  className="flex items-center justify-between rounded-xl border p-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{community.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {community.timezone}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Creado {formatDate(community.createdAt)}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="gap-1">
                        <Users className="h-3 w-3" />
                        {community._count.members}{" "}
                        {community._count.members === 1
                          ? "miembro"
                          : "miembros"}
                      </Badge>
                      <Badge variant="outline" className="gap-1">
                        <Home className="h-3 w-3" />
                        {community._count.units}{" "}
                        {community._count.units === 1 ? "unidad" : "unidades"}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Dialog
                      open={managingMembersCommunityId === community.id}
                      onOpenChange={(open) => {
                        setManagingMembersCommunityId(
                          open ? community.id : null,
                        );
                        if (!open) {
                          setNewMember({
                            userId: "",
                            role: "RESIDENT",
                            unitId: "",
                            newUnit: null,
                            assignmentType: "OWNER",
                          });
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Gestionar miembros"
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Gestionar miembros</DialogTitle>
                          <DialogDescription>
                            Agregá o eliminá miembros de {community.name}.
                          </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                          {/* Add Member Form */}
                          <div className="rounded-xl border p-4">
                            <h4 className="mb-3 text-sm font-medium">
                              Agregar miembro
                            </h4>
                            <div className="space-y-3">
                              <div className="flex gap-2">
                                <Select
                                  value={newMember.userId}
                                  onValueChange={(value) =>
                                    setNewMember({
                                      ...newMember,
                                      userId: value,
                                    })
                                  }
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Seleccionar usuario" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableUsers?.map((user) => (
                                      <SelectItem key={user.id} value={user.id}>
                                        {user.name || user.email}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Select
                                  value={newMember.role}
                                  onValueChange={(value: Role) =>
                                    setNewMember({ ...newMember, role: value })
                                  }
                                >
                                  <SelectTrigger className="w-[160px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ROLES.map((role) => (
                                      <SelectItem
                                        key={role.value}
                                        value={role.value}
                                      >
                                        {role.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <div className="flex gap-2">
                                  <Select
                                    value={
                                      newMember.newUnit
                                        ? "__new__"
                                        : newMember.unitId || "__none__"
                                    }
                                    onValueChange={(value) => {
                                      if (value === "__none__") {
                                        setNewMember({
                                          ...newMember,
                                          unitId: "",
                                          newUnit: null,
                                        });
                                      } else if (value === "__new__") {
                                        setNewMember({
                                          ...newMember,
                                          unitId: "",
                                          newUnit: {
                                            unitNumber: "",
                                            blockSection: "",
                                          },
                                        });
                                      } else {
                                        setNewMember({
                                          ...newMember,
                                          unitId: value,
                                          newUnit: null,
                                        });
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="flex-1">
                                      <SelectValue placeholder="Seleccionar unidad (opcional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">
                                        Sin unidad
                                      </SelectItem>
                                      {communityDetails?.units?.map((unit) => (
                                        <SelectItem
                                          key={unit.id}
                                          value={unit.id}
                                        >
                                          {unit.unitNumber}
                                          {unit.blockSection &&
                                            ` (${unit.blockSection})`}
                                        </SelectItem>
                                      ))}
                                      <SelectItem value="__new__">
                                        + Crear nueva unidad
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {(newMember.unitId || newMember.newUnit) && (
                                    <Select
                                      value={newMember.assignmentType}
                                      onValueChange={(value: AssignmentType) =>
                                        setNewMember({
                                          ...newMember,
                                          assignmentType: value,
                                        })
                                      }
                                    >
                                      <SelectTrigger className="w-[160px]">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {ASSIGNMENT_TYPES.map((type) => (
                                          <SelectItem
                                            key={type.value}
                                            value={type.value}
                                          >
                                            {type.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                                {newMember.newUnit && (
                                  <div className="flex gap-2">
                                    <Input
                                      placeholder="Número de unidad"
                                      value={newMember.newUnit.unitNumber}
                                      onChange={(e) =>
                                        setNewMember({
                                          ...newMember,
                                          newUnit: {
                                            ...newMember.newUnit!,
                                            unitNumber: e.target.value,
                                          },
                                        })
                                      }
                                      className="flex-1"
                                    />
                                    <Input
                                      placeholder="Manzana/Sector (opcional)"
                                      value={newMember.newUnit.blockSection}
                                      onChange={(e) =>
                                        setNewMember({
                                          ...newMember,
                                          newUnit: {
                                            ...newMember.newUnit!,
                                            blockSection: e.target.value,
                                          },
                                        })
                                      }
                                      className="flex-1"
                                    />
                                  </div>
                                )}
                              </div>
                              <Button
                                onClick={handleAddMember}
                                disabled={
                                  addMember.isPending || !newMember.userId
                                }
                                className="w-full"
                              >
                                {addMember.isPending ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Plus className="mr-2 h-4 w-4" />
                                )}
                                Agregar miembro
                              </Button>
                            </div>
                            {availableUsers?.length === 0 && (
                              <p className="mt-2 text-xs text-muted-foreground">
                                Todos los usuarios ya son miembros de este
                                barrio.
                              </p>
                            )}
                          </div>

                          <div>
                            <h4 className="mb-3 text-sm font-medium">
                              Miembros actuales (
                              {communityDetails?.members.length || 0})
                            </h4>
                            {detailsLoading ? (
                              <div className="flex justify-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                              </div>
                            ) : communityDetails?.members.length === 0 ? (
                              <p className="py-4 text-center text-sm text-muted-foreground">
                                Todavía no hay miembros.
                              </p>
                            ) : (
                              <div className="max-h-[400px] space-y-2 overflow-y-auto">
                                {communityDetails?.members.map((member) => (
                                  <div
                                    key={member.id}
                                    className="rounded-xl border"
                                  >
                                    <div className="flex items-center justify-between px-3 py-2">
                                      <div className="flex items-center gap-3">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-medium">
                                          {member.user.name
                                            ?.slice(0, 2)
                                            .toUpperCase() ||
                                            member.user.email
                                              ?.slice(0, 2)
                                              .toUpperCase()}
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium">
                                            {member.user.name ||
                                              member.user.email}
                                          </p>
                                          <div className="flex items-center gap-2">
                                            <p className="text-xs text-muted-foreground">
                                              {member.user.email}
                                            </p>
                                            {member.unitAssignments.length >
                                              0 && (
                                              <Badge
                                                variant="outline"
                                                className="text-xs"
                                              >
                                                <Home className="mr-1 h-3 w-3" />
                                                {member.unitAssignments.length}{" "}
                                                unidad
                                                {member.unitAssignments.length >
                                                1
                                                  ? "es"
                                                  : ""}
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Badge
                                          variant="secondary"
                                          className="text-xs"
                                        >
                                          {ROLES.find(
                                            (r) => r.value === member.role,
                                          )?.label || member.role}
                                        </Badge>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => {
                                            if (
                                              expandedMemberId === member.id
                                            ) {
                                              setExpandedMemberId(null);
                                              setMemberUnitForm({
                                                unitId: "",
                                                newUnit: null,
                                                assignmentType: "OWNER",
                                              });
                                            } else {
                                              setExpandedMemberId(member.id);
                                              setMemberUnitForm({
                                                unitId: "",
                                                newUnit: null,
                                                assignmentType: "OWNER",
                                              });
                                            }
                                          }}
                                        >
                                          {expandedMemberId === member.id ? (
                                            <ChevronUp className="h-4 w-4" />
                                          ) : (
                                            <ChevronDown className="h-4 w-4" />
                                          )}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() =>
                                            handleRemoveMember(member.id)
                                          }
                                          disabled={
                                            removeMember.isPending &&
                                            removeMember.variables?.memberId ===
                                              member.id
                                          }
                                        >
                                          {removeMember.isPending &&
                                          removeMember.variables?.memberId ===
                                            member.id ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <X className="h-4 w-4" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>

                                    {expandedMemberId === member.id && (
                                      <div className="border-t px-3 py-3 space-y-3">
                                        {member.unitAssignments.length > 0 && (
                                          <div className="space-y-2">
                                            <p className="text-xs font-medium text-muted-foreground">
                                              Unidades actuales
                                            </p>
                                            {member.unitAssignments.map(
                                              (assignment) => (
                                                <div
                                                  key={assignment.id}
                                                  className="flex items-center justify-between rounded bg-muted/50 px-2 py-1.5"
                                                >
                                                  <div className="flex items-center gap-2">
                                                    <Home className="h-3 w-3 text-muted-foreground" />
                                                    <span className="text-sm">
                                                      {
                                                        assignment.unit
                                                          .unitNumber
                                                      }
                                                      {assignment.unit
                                                        .blockSection &&
                                                        ` (${assignment.unit.blockSection})`}
                                                    </span>
                                                    <Badge
                                                      variant="outline"
                                                      className="text-xs"
                                                    >
                                                      {ASSIGNMENT_TYPES.find(
                                                        (t) =>
                                                          t.value ===
                                                          assignment.type,
                                                      )?.label ||
                                                        assignment.type}
                                                    </Badge>
                                                  </div>
                                                  <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6"
                                                    onClick={() =>
                                                      handleRemoveAssignment(
                                                        assignment.id,
                                                      )
                                                    }
                                                    disabled={
                                                      removeAssignment.isPending &&
                                                      removeAssignment.variables
                                                        ?.assignmentId ===
                                                        assignment.id
                                                    }
                                                  >
                                                    {removeAssignment.isPending &&
                                                    removeAssignment.variables
                                                      ?.assignmentId ===
                                                      assignment.id ? (
                                                      <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                      <X className="h-3 w-3" />
                                                    )}
                                                  </Button>
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        )}

                                        <div className="space-y-2">
                                          <p className="text-xs font-medium text-muted-foreground">
                                            Agregar asignación de unidad
                                          </p>
                                          <div className="flex gap-2">
                                            <Select
                                              value={
                                                memberUnitForm.newUnit
                                                  ? "__new__"
                                                  : memberUnitForm.unitId ||
                                                    "__none__"
                                              }
                                              onValueChange={(value) => {
                                                if (value === "__none__") {
                                                  setMemberUnitForm({
                                                    ...memberUnitForm,
                                                    unitId: "",
                                                    newUnit: null,
                                                  });
                                                } else if (
                                                  value === "__new__"
                                                ) {
                                                  setMemberUnitForm({
                                                    ...memberUnitForm,
                                                    unitId: "",
                                                    newUnit: {
                                                      unitNumber: "",
                                                      blockSection: "",
                                                    },
                                                  });
                                                } else {
                                                  setMemberUnitForm({
                                                    ...memberUnitForm,
                                                    unitId: value,
                                                    newUnit: null,
                                                  });
                                                }
                                              }}
                                            >
                                              <SelectTrigger className="flex-1">
                                                <SelectValue placeholder="Seleccionar unidad" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="__none__">
                                                  Seleccionar una unidad
                                                </SelectItem>
                                                {communityDetails?.units?.map(
                                                  (unit) => (
                                                    <SelectItem
                                                      key={unit.id}
                                                      value={unit.id}
                                                    >
                                                      {unit.unitNumber}
                                                      {unit.blockSection &&
                                                        ` (${unit.blockSection})`}
                                                    </SelectItem>
                                                  ),
                                                )}
                                                <SelectItem value="__new__">
                                                  + Crear nueva unidad
                                                </SelectItem>
                                              </SelectContent>
                                            </Select>
                                            {(memberUnitForm.unitId ||
                                              memberUnitForm.newUnit) && (
                                              <Select
                                                value={
                                                  memberUnitForm.assignmentType
                                                }
                                                onValueChange={(
                                                  value: AssignmentType,
                                                ) =>
                                                  setMemberUnitForm({
                                                    ...memberUnitForm,
                                                    assignmentType: value,
                                                  })
                                                }
                                              >
                                                <SelectTrigger className="w-[140px]">
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {ASSIGNMENT_TYPES.map(
                                                    (type) => (
                                                      <SelectItem
                                                        key={type.value}
                                                        value={type.value}
                                                      >
                                                        {type.label}
                                                      </SelectItem>
                                                    ),
                                                  )}
                                                </SelectContent>
                                              </Select>
                                            )}
                                          </div>
                                          {memberUnitForm.newUnit && (
                                            <div className="flex gap-2">
                                              <Input
                                                placeholder="Número de unidad"
                                                value={
                                                  memberUnitForm.newUnit
                                                    .unitNumber
                                                }
                                                onChange={(e) =>
                                                  setMemberUnitForm({
                                                    ...memberUnitForm,
                                                    newUnit: {
                                                      ...memberUnitForm.newUnit!,
                                                      unitNumber:
                                                        e.target.value,
                                                    },
                                                  })
                                                }
                                                className="flex-1"
                                              />
                                              <Input
                                                placeholder="Manzana/Sector"
                                                value={
                                                  memberUnitForm.newUnit
                                                    .blockSection
                                                }
                                                onChange={(e) =>
                                                  setMemberUnitForm({
                                                    ...memberUnitForm,
                                                    newUnit: {
                                                      ...memberUnitForm.newUnit!,
                                                      blockSection:
                                                        e.target.value,
                                                    },
                                                  })
                                                }
                                                className="flex-1"
                                              />
                                            </div>
                                          )}
                                          {(memberUnitForm.unitId ||
                                            memberUnitForm.newUnit
                                              ?.unitNumber) && (
                                            <Button
                                              size="sm"
                                              onClick={() =>
                                                handleAssignUnit(member.id)
                                              }
                                              disabled={assignUnit.isPending}
                                              className="w-full"
                                            >
                                              {assignUnit.isPending ? (
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                              ) : (
                                                <Plus className="mr-2 h-4 w-4" />
                                              )}
                                              Asignar unidad
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Dialog
                      open={editingCommunity?.id === community.id}
                      onOpenChange={(open) =>
                        setEditingCommunity(
                          open
                            ? {
                                id: community.id,
                                name: community.name,
                                timezone: community.timezone,
                              }
                            : null,
                        )
                      }
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Editar barrio"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Editar barrio</DialogTitle>
                          <DialogDescription>
                            Actualizá los datos del barrio.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="edit-name">Nombre</Label>
                            <Input
                              id="edit-name"
                              value={editingCommunity?.name || ""}
                              onChange={(e) =>
                                setEditingCommunity(
                                  editingCommunity
                                    ? {
                                        ...editingCommunity,
                                        name: e.target.value,
                                      }
                                    : null,
                                )
                              }
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-timezone">Zona horaria</Label>
                            <Input
                              id="edit-timezone"
                              value={editingCommunity?.timezone || ""}
                              onChange={(e) =>
                                setEditingCommunity(
                                  editingCommunity
                                    ? {
                                        ...editingCommunity,
                                        timezone: e.target.value,
                                      }
                                    : null,
                                )
                              }
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button
                            onClick={handleUpdate}
                            disabled={updateCommunity.isPending}
                          >
                            {updateCommunity.isPending
                              ? "Guardando..."
                              : "Guardar"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Eliminar barrio"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar barrio?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esto eliminará de forma permanente &quot;
                            {community.name}&quot; y todos los datos asociados
                            (miembros, unidades, invitaciones, etc.). Esta
                            acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(community.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
